import React, { FC, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  LearnablePronunciationData,
  LearnablePronunciationScore,
} from 'src/api/userDictionary/types';
import { useKeyboardShortcut } from 'src/hooks';
import { AudioValidationResult } from '../utils/validateAudioBuffer';
import soundFile from './assets/correct_answer.mp3';
import { PronunciationViewWrapper } from './PronunciationViewWrapper';
import { LearnableInformation } from './PronunciationLearnableView';
import * as SL from './layout-styles';
import * as S from './styles';

const msgs = defineMessages({
  instruction: { id: 'pronunciationBuddy_testCard_description' },
  analysing: { id: 'pronunciationBuddy_analysing_description' },
  tooShort: { id: 'pronunicationBuddy_messageTooShort_description' },
  noOrLowAudio: { id: 'pronunicationBuddy_noOrLowAudio_description' },
  scorePass: { id: 'pronunciationBuddy_positiveOutcome_description' },
  scoreAlmost: { id: 'pronunciationBuddy_mediumOutcome_description' },
  scoreFail: { id: 'pronunciationBuddy_badOutcome_description' },
});

const ANALYSIS_STATE = {
  INVALID: 'INVALID',
  PENDING: 'PENDING',
  DONE: 'DONE',
  RETRY: 'RETRY',
} as const;

export const PronunciationAnalysisView: FC<{
  learnable: LearnablePronunciationData;
  score: LearnablePronunciationScore | null;
  validationResult: AudioValidationResult;
  continueButtons: React.ReactNode;
  onError: () => void;
  /** Advances the session when Space or Enter is pressed on a successful result. */
  onContinue?: () => void;
  /** Renders a smaller, self-contained layout without the instruction copy or
   * target/source text, for embedding alongside a screen that already shows
   * that information. */
  compact?: boolean;
}> = ({
  learnable,
  score,
  validationResult,
  continueButtons,
  onError,
  onContinue,
  compact = false,
}) => {
  const intl = useIntl();
  const instructionText = intl.formatMessage(msgs.instruction);

  const analysisState = useMemo(() => {
    if (score && validationResult !== 'valid') {
      console.error('Invalid pronunciation analysis state', score, validationResult);
      onError();
      return null;
    }

    if (validationResult !== 'valid') return ANALYSIS_STATE.INVALID;

    if (!score) return ANALYSIS_STATE.PENDING;

    if (score.result === 'pass') return ANALYSIS_STATE.DONE;

    return ANALYSIS_STATE.RETRY;
  }, [score, validationResult, onError]);

  const isDoneState = analysisState === ANALYSIS_STATE.DONE;
  useKeyboardShortcut('enter', isDoneState ? onContinue : undefined);
  useKeyboardShortcut('space', isDoneState ? onContinue : undefined);

  const analysisMessage = useMemo(() => {
    if (validationResult === 'too-short') return msgs.tooShort;

    if (validationResult === 'silent') return msgs.noOrLowAudio;

    if (!score) return msgs.analysing;

    switch (score.result) {
      case 'pass':
        return msgs.scorePass;
      case 'almost':
        return msgs.scoreAlmost;
      case 'fail':
        return msgs.scoreFail;
      default: {
        console.error(`Invalid pronunciation analysis result: ${score.result}`);
        onError();
      }
    }
  }, [validationResult, score, onError]);

  if (!analysisState || !analysisMessage) {
    return null;
  }

  if (compact) {
    return (
      <PronunciationViewWrapper $background={isDoneState ? 'success98' : 'default'}>
        <SL.CompactLayout>
          <S.InstructionText>{intl.formatMessage(analysisMessage)}</S.InstructionText>
          {analysisState === ANALYSIS_STATE.PENDING || isDoneState ? (
            isDoneState ? (
              <>
                {/* decorative sound SFX, captions not applicable */}
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio autoPlay preload="auto" src={soundFile} data-testid="correct-answer-sound" />
                <S.CompactCircleElement $color="success80">
                  <S.CompactAnalysisIcon name="tickFilled" $color="success0" />
                </S.CompactCircleElement>
                {continueButtons ? (
                  <SL.CompactContinueButtonsContainer>
                    {continueButtons}
                  </SL.CompactContinueButtonsContainer>
                ) : null}
              </>
            ) : (
              <S.CompactCircleElement $color="tertiary60">
                <S.CompactAnalysisIcon name="search" $color="secondary20" />
              </S.CompactCircleElement>
            )
          ) : null}
        </SL.CompactLayout>
      </PronunciationViewWrapper>
    );
  }

  if (analysisState === ANALYSIS_STATE.PENDING || isDoneState) {
    return (
      <PronunciationViewWrapper $background={isDoneState ? 'success98' : 'default'}>
        <SL.PronunciationLayout>
          <SL.FullRowContainer />
          <SL.FullRowContainer>
            <SL.HiddenContent>
              <S.InstructionText>{instructionText}</S.InstructionText>
            </SL.HiddenContent>
          </SL.FullRowContainer>
          <SL.FullRowContainer $wide>
            <SL.HiddenContent>
              <LearnableInformation learnable={learnable} />

              <SL.OverrideContent>
                <S.MainText>{intl.formatMessage(analysisMessage)}</S.MainText>
              </SL.OverrideContent>
            </SL.HiddenContent>
          </SL.FullRowContainer>
          {isDoneState ? (
            <>
              <SL.FullRowContainer>
                {/* decorative sound SFX, captions not applicable */}
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio autoPlay preload="auto" src={soundFile} data-testid="correct-answer-sound" />
                <S.CircleElement $color="success80">
                  <S.AnalysisIcon name="tickFilled" $color="success0" />
                </S.CircleElement>
              </SL.FullRowContainer>
              <SL.ContinueButtonsContainer>{continueButtons}</SL.ContinueButtonsContainer>
            </>
          ) : (
            <SL.FullRowContainer>
              <S.CircleElement $color="tertiary60">
                <S.AnalysisIcon name="search" $color="secondary20" />
              </S.CircleElement>
            </SL.FullRowContainer>
          )}
        </SL.PronunciationLayout>
      </PronunciationViewWrapper>
    );
  }

  return (
    <PronunciationViewWrapper>
      <SL.AnalysisLayout>
        <S.MainText>{intl.formatMessage(analysisMessage)}</S.MainText>
      </SL.AnalysisLayout>
    </PronunciationViewWrapper>
  );
};
